/* istanbul ignore file */
import { useEmployeeService } from 'domain/shared/employee';
import { EMPTY_VALUE_INDICATOR, useAsyncValue } from 'framework/utils';
import { isPolishOffice } from 'domain/shared/employee';

const useLeavesErrorMap = () => {
  const { getCurrentEmployee } = useEmployeeService();
  const employee = useAsyncValue(async () => getCurrentEmployee(), []);
  const isPolishEmployee = employee && isPolishOffice(String(employee.officeId));

  const leavePolicy = isPolishEmployee ? (
    <></>
  ) : (
    <>
      {' '}
      (
      <a href="https://davinci.syberry.com/handbook/leave-policy-belarus.md" target="_blank" rel="noopener noreferrer">
        Leave Policy for Employees
      </a>
      )
    </>
  );

  return {
    'LEAVE-BR-1': isPolishEmployee
      ? 'You must have an active contract to submit a leave.'
      : 'You must have an active contract to submit a leave (article 154 of the labor code of the Republic of Belarus).',
    'LEAVE-BR-2': (available: any) => (
      <>
        Requested paid leave contains more days than you have available: {available}
        {leavePolicy}.
      </>
    ),
    'LEAVE-BR-3': (available: any) => (
      <>
        Requested sick day contains more days than you have available:&nbsp;{available}
        {leavePolicy}.
      </>
    ),
    'LEAVE-BR-4': (available: any) => (
      <>
        Requested day on demands contains more days than you have available:&nbsp;{available}
        {leavePolicy}.
      </>
    ),
    'LEAVE-BR-5': <>Sick day cannot be longer than 1 day{leavePolicy}.</>,

    'LEAVE-BR-6': <>Paid leave duration must be between 10 and 15 calendar days{leavePolicy}.</>,

    'LEAVE-BR-7-BY': (
      <>Paid leave cannot be taken earlier than 6 months after you started working at Syberry{leavePolicy}.</>
    ),

    'LEAVE-BR-7-US': <>This leave is requested less than 1 month after your employment start date{leavePolicy}.</>,

    'LEAVE-BR-7-PL': (
      <>
        Paid leave can be taken only 1 month after an employee signed their first contract with the Polish office
        {leavePolicy}.
      </>
    ),

    'LEAVE-BR-8': (
      <>The gap between this leave and another leave longer than 3 days is less than 3 months{leavePolicy}.</>
    ),

    'LEAVE-BR-9': (
      <>
        Paid leave must be requested not earlier than 3 months and not later than 1 month before its start date
        {leavePolicy}.
      </>
    ),

    'LEAVE-BR-10': (
      <>
        Unpaid leave with duration more than 2 business days must be requested at least 1 week before its start date
        {leavePolicy}.
      </>
    ),

    'LEAVE-BR-10-PL-1': (
      <>
        Unpaid leave with duration more than 2 business days must be requested at least 1 week before its start date
        {leavePolicy}.
      </>
    ),

    'LEAVE-BR-10-PL-2': (
      <>
        Unpaid leave with duration more than 2 business days must be requested at least 1 month before its start date
        {leavePolicy}.
      </>
    ),

    'LEAVE-BR-11': (
      <>
        Paid leave must start in the current working year because you have available days for paid leave in this year
        {leavePolicy}.
      </>
    ),

    'LEAVE-BR-12': <>Dates of requested leave overlap already created leaves of the same type{leavePolicy}.</>,

    'LEAVE-BR-13': <>Dates of requested leave overlap already created leaves{leavePolicy}.</>,

    'LEAVE-BR-13-1': (
      <>Dates of requested unpaid leave overlap already created paid leaves or sick days{leavePolicy}.</>
    ),

    'LEAVE-BR-13-2': (
      <>Dates of requested paid leave overlap already created unpaid leaves or sick days{leavePolicy}.</>
    ),

    'LEAVE-BR-13-3': <>Requested sick day overlap already created paid leaves or unpaid leaves{leavePolicy}.</>,

    'LEAVE-BR-13-4': (
      <>Dates of requested leave compensation overlap already created unpaid leaves or sick days{leavePolicy}.</>
    ),

    'LEAVE-BR-13-5': <>Dates of requested leave compensation overlap already created leaves{leavePolicy}.</>,

    'LEAVE-BR-13-6': <>Dates of requested leave compensation overlap already created leaves{leavePolicy}.</>,

    'LEAVE-BR-14': (
      <>
        You don’t have days available for paid leave in the current working year. Days from the next working year are
        still available{leavePolicy}.
      </>
    ),

    'LEAVE-BR-15-1': <>Requested military leave overlaps already created leaves of other types{leavePolicy}.</>,

    'LEAVE-BR-15-2': <>Requested leave overlaps already created military leave{leavePolicy}.</>,

    'LEAVE-BR-16': <>You can have no more than 3 Paid Leaves during one leave period{leavePolicy}.</>,

    'LEAVE-BR-17': <>At least one Paid Leave during the leave period must be longer than 13 days{leavePolicy}.</>,

    'LEAVE-BR-17-BY': <>At least one Paid Leave during the leave period must be longer than 13 days{leavePolicy}.</>,

    'LEAVE-BR-17-PL': <>At least one Paid Leave during the leave period must be longer than 13 days{leavePolicy}.</>,

    'LEAVE-BR-18': <>You must plan at least 2 Paid Leaves during one leave period{leavePolicy}.</>,

    'LEAVE-BR-19': (_projectId: string, projectName: string, startDate: string, endDate: string) => (
      <>
        Leave is requested during a critical period for the project {projectName || EMPTY_VALUE_INDICATOR}. Critical
        period from {startDate || EMPTY_VALUE_INDICATOR} to {endDate || EMPTY_VALUE_INDICATOR}.
      </>
    ),

    'LEAVE-BR-20': (
      <>The total duration of Day On Demand leaves cannot exceed 4 days during one leave period{leavePolicy}.</>
    ),

    'LEAVE-BR-21': <>Paid leave cannot start or end on a Belarusian public holiday{leavePolicy}.</>,

    'LEAVE-BR-22': <>Leave cannot be requested without an active contract for the requested period{leavePolicy}.</>,

    'LEAVE-BR-23': <>Leave period must not overlap with contracts that have a different type or office{leavePolicy}.</>,

    LEAVES_EXTENSION_DATE_FORBIDDEN: 'Leave extension is allowed for Paid Leaves only',
  };
};

export default useLeavesErrorMap;
